/* ==========================================================================
   Novatto — Globo Quick View Modal (Dawn 15.4) — Sections-based
   --------------------------------------------------------------------------
   ✅ Prevents Globo inline quickView injection below footer
   ✅ Loads product main section via ?sections=
   ✅ Executes injected inline scripts (variant icons, etc.)
   ✅ Syncs variant selection back to collection links (?variant=)
   ✅ Removes stray pickup-availability-drawer outside modal
   ✅ Spoofs ShopifyAnalytics.meta so apps see product context:
        - meta.page.pageType/resourceType/resourceId
        - meta.product
        - meta.selectedVariantId

   ✅ Snap Compare (no embed injector needed):
        - Adds a custom "Add to compare" button inside Quick View only
        - Writes to localStorage: snap-compare-list (correct format)
        - Dispatches StorageEvent + common update events for live UI refresh
        - Builds payload from /products/<handle>.js (consistent core fields)
        - Augments payload with DOM-derived Description + Type (matches product-page tabs)
        - Provides fallback to ShopifyAnalytics.meta + modal DOM if needed

   ✅ Dawn 15.4 Media/Gallery Modal safety:
        - Claims any body-level media modals spawned while Quick View is open
        - Removes ONLY those claimed modals on close
        - Never breaks gallery modal on real product pages
        - Never removes template-product unless Quick View added it

   INCLUDE (theme.liquid before </body>):
     <script src="{{ 'novatto-globo-quickview-sections.js' | asset_url }}" defer></script>
   ========================================================================== */
(() => {
  const DEBUG = false;
  const log = (...a) => DEBUG && console.log('[NOV-QV]', ...a);

  // ---------------------------
  // Helpers
  // ---------------------------
  function toRelative(pathOrUrl) {
    let src = pathOrUrl || '';
    try {
      if (src.startsWith('http')) {
        const u = new URL(src);
        src = u.pathname + u.search + u.hash;
      }
    } catch (e) {}
    if (!src.startsWith('/')) src = '/' + src;
    return src;
  }

  function getHandleFromPath(path) {
    const parts = path.split('?')[0].split('/').filter(Boolean);
    const idx = parts.indexOf('products');
    return idx >= 0 ? parts[idx + 1] : null;
  }

  function getVariantFromPath(path) {
    try {
      const u = new URL(path, window.location.origin);
      return u.searchParams.get('variant');
    } catch (e) {
      return null;
    }
  }

  function renderError(bodyEl, title, details) {
    bodyEl.innerHTML = `
      <div style="padding:18px;">
        <div style="font-weight:700;margin-bottom:8px;">${title}</div>
        <pre style="white-space:pre-wrap;word-break:break-word;background:#f6f7fb;padding:12px;border-radius:10px;max-height:40vh;overflow:auto;">${details || ''}</pre>
      </div>
    `;
  }

  function executeScripts(container) {
    const scripts = Array.from(container.querySelectorAll('script'));
    scripts.forEach((old) => {
      const src = old.getAttribute('src');
      const type = (old.getAttribute('type') || '').trim();

      // Keep JSON inert
      if (
        type === 'application/json' ||
        type === 'application/ld+json' ||
        old.hasAttribute('data-nov-product-variants') ||
        old.hasAttribute('data-selected-variant')
      ) return;

      const s = document.createElement('script');
      if (type) s.type = type;

      if (src) {
        const abs = new URL(src, window.location.origin).href;
        // Avoid duplicate script load of identical URL
        if (document.querySelector(`script[src="${CSS.escape(abs)}"]`)) {
          old.remove();
          return;
        }
        s.src = abs;
        s.async = false;
        document.head.appendChild(s);
        old.remove();
        return;
      }

      // Inline: reinsert to execute
      s.textContent = old.textContent || '';
      document.body.appendChild(s);
      old.remove();
    });
  }

  function nudgeInit() {
    try {
      document.dispatchEvent(new Event('shopify:section:load', { bubbles: true }));
    } catch (e) {}

    try {
      document.dispatchEvent(new Event('variant:change', { bubbles: true }));
    } catch (e) {}

    /*
      Do NOT dispatch a generic "change" event on document.
      Globo expects change targets to be Elements and calls target.closest().
    */
    try {
      const overlay = document.querySelector('.nov-qv-overlay.is-open');
      const body = overlay?.querySelector?.('.nov-qv-body');
      const idInput =
        body?.querySelector?.('form[action*="/cart/add"] input[name="id"]') ||
        body?.querySelector?.('input[name="id"]') ||
        null;

      if (idInput) {
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } catch (e) {}

    try {
      window.dispatchEvent(new Event('resize'));
    } catch (e) {}
  }

  // ---------------------------
  // Globo kill + cleanup (prevents footer injection)
  // ---------------------------
  function stripInlineQuickViewOnclicks(root = document) {
    root
      .querySelectorAll('a.open-quick-view[onclick], a[onclick*="quickView("], a[onclick*="globofilter.quickView"]')
      .forEach((a) => {
        a.setAttribute('data-nov-onclick', a.getAttribute('onclick'));
        a.removeAttribute('onclick');
      });
  }

  function overrideGloboQuickView() {
    window.globofilter = window.globofilter || {};
    try {
      window.globofilter.quickView = function () { return false; };
      window.globofilter.quickView._novDisabled = true;
    } catch (e) {}
    stripInlineQuickViewOnclicks(document);
  }

  function removeInjectedAfterFooterJunk() {
    const overlay = document.querySelector('.nov-qv-overlay');

    // Don't run destructive cleanup on actual product pages unless Quick View is open
    if (document.body.classList.contains('template-product') && !(overlay && overlay.classList.contains('is-open'))) {
      return;
    }

    // Remove pickup drawer outside modal
    document.querySelectorAll('pickup-availability-drawer').forEach((node) => {
      if (overlay && overlay.contains(node)) return;
      node.remove();
    });

    // Common Globo QV containers that might get appended to body
    [
      '.gfqv', '.gf-qv', '.gf-quick-view', '.gf-quickview',
      '.gf-quick-view-wrapper', '.gf-quick-view-container',
      '.gf-quick-view-overlay', '.gf-quick-view-modal',
      '[id*="gfqv"]', '[id*="gf-qv"]'
    ].forEach((sel) => {
      document.querySelectorAll(sel).forEach((n) => {
        if (overlay && overlay.contains(n)) return;
        n.remove();
      });
    });

    // Stray modals/components outside main content
    // IMPORTANT: Do NOT remove Dawn's legit product media modal/lightbox.
    // We only remove clearly stray product-info / product-popup-modal outside #MainContent.
    [
      'product-info',
      'modal-dialog.product-popup-modal'
      // NOTE: intentionally NOT including 'product-modal' or '.product-media-modal' or 'media-modal'
    ].forEach((sel) => {
      document.querySelectorAll(sel).forEach((node) => {
        if (overlay && overlay.contains(node)) return;

        const insideMain = !!node.closest('#MainContent');
        if (insideMain) return;

        node.remove();
      });
    });

    // Occasionally apps append entire sections to body
    document.querySelectorAll('body > .shopify-section').forEach((sec) => {
      if (overlay && overlay.contains(sec)) return;
      if (sec.querySelector && (sec.querySelector('product-info') || sec.querySelector('pickup-availability-drawer'))) {
        sec.remove();
      }
    });

    // Never remove Dawn media/gallery modals unless explicitly owned by Quick View
    // (Owned cleanup is handled in stopQuickViewModalOwnership)
  }

  function enforceKillAndClean() {
    overrideGloboQuickView();
    removeInjectedAfterFooterJunk();
  }

  enforceKillAndClean();
  setTimeout(enforceKillAndClean, 200);
  setTimeout(enforceKillAndClean, 900);

  const guard = new MutationObserver((muts) => {
    if (!muts.some(m => m.addedNodes && m.addedNodes.length)) return;
    enforceKillAndClean();
  });
  guard.observe(document.documentElement, { childList: true, subtree: true });

  // ---------------------------
  // Product context spoof (URL + body class + ShopifyAnalytics.meta)
  // ---------------------------
  const ctx = {
    originalPath: null,
    spoofedUrl: false,
    analyticsSaved: null,

    // NEW: track whether Quick View added template-product
    addedTemplateClass: false
  };

  function getMeta() {
    try { return window.ShopifyAnalytics?.meta || null; } catch (e) { return null; }
  }

  function ensureMetaPage(meta) {
    meta.page = meta.page || {};
    return meta.page;
  }

  function findMetaProductByHandle(meta, handle) {
    const list = Array.isArray(meta.products) ? meta.products : [];
    return list.find(p => p && p.handle === handle) || null;
  }

  function getModalVariantIdForMeta(overlay) {
    const body = overlay?.querySelector?.('.nov-qv-body');
    if (!body) return null;

    const input =
      body.querySelector('form[data-type="add-to-cart-form"] input[name="id"]') ||
      body.querySelector('product-form form[action*="/cart/add"] input[name="id"]') ||
      body.querySelector('form[action*="/cart/add"] input[name="id"]') ||
      body.querySelector('input[name="id"]');

    const v = input?.value ? String(input.value).trim() : '';
    return v || null;
  }

  function saveAnalyticsSnapshotOnce() {
    const meta = getMeta();
    if (!meta || ctx.analyticsSaved) return;

    const page = ensureMetaPage(meta);

    ctx.analyticsSaved = {
      pageType: page.pageType,
      resourceType: page.resourceType,
      resourceId: page.resourceId,
      selectedVariantId: meta.selectedVariantId,
      product: meta.product
    };
  }

  function restoreShopifyAnalytics() {
    const meta = getMeta();
    if (!meta || !ctx.analyticsSaved) return;

    const page = ensureMetaPage(meta);
    const s = ctx.analyticsSaved;

    try {
      page.pageType = s.pageType;
      page.resourceType = s.resourceType;
      page.resourceId = s.resourceId;

      meta.selectedVariantId = s.selectedVariantId;
      meta.product = s.product;
    } catch (e) {}

    ctx.analyticsSaved = null;
  }

  function syncAnalyticsForModal(overlay, handle) {
    const meta = getMeta();
    if (!meta || !handle) return;

    saveAnalyticsSnapshotOnce();

    const page = ensureMetaPage(meta);
    const prod = findMetaProductByHandle(meta, handle);

    page.pageType = 'product';
    page.resourceType = 'product';

    if (prod?.id) page.resourceId = prod.id;
    if (prod) meta.product = prod;

    const vId = getModalVariantIdForMeta(overlay);
    if (vId) meta.selectedVariantId = vId;
  }

  function enableProductContext(handle, variantId) {
    // Only add template-product if it isn't already present
    const had = document.body.classList.contains('template-product');
    if (!had) {
      document.body.classList.add('template-product');
      document.documentElement.classList.add('template-product');
      ctx.addedTemplateClass = true;
    } else {
      ctx.addedTemplateClass = false;
    }

    // Spoof URL to product path (no reload)
    if (handle && !ctx.spoofedUrl) {
      ctx.originalPath = window.location.pathname + window.location.search + window.location.hash;

      const u = new URL(`/products/${handle}`, window.location.origin);
      if (variantId) u.searchParams.set('variant', String(variantId));

      try {
        history.replaceState(history.state, document.title, u.pathname + u.search + u.hash);
        ctx.spoofedUrl = true;
      } catch (e) {}
    }
  }

  function disableProductContext() {
    // Only remove template-product if WE added it
    if (ctx.addedTemplateClass) {
      document.body.classList.remove('template-product');
      document.documentElement.classList.remove('template-product');
    }
    ctx.addedTemplateClass = false;

    restoreShopifyAnalytics();

    if (ctx.spoofedUrl && ctx.originalPath) {
      try {
        history.replaceState(history.state, document.title, ctx.originalPath);
      } catch (e) {}
    }

    ctx.spoofedUrl = false;
    ctx.originalPath = null;
  }

  // ---------------------------
  // Dawn 15.4 gallery/media modal ownership
  // (prevents "gallery modal under footer" after Quick View)
  // ---------------------------
  function startQuickViewModalOwnership(overlay) {
    if (!overlay) return;

    // Clean any prior observer
    if (overlay._novOwnedObserver) {
      try { overlay._novOwnedObserver.disconnect(); } catch (e) {}
      overlay._novOwnedObserver = null;
    }

    overlay._novOwnedNodes = overlay._novOwnedNodes || new Set();

    const isQuickViewOpen = () => overlay.classList.contains('is-open');

    const maybeOwn = (node) => {
      if (!node || node.nodeType !== 1) return;

      // Already inside our overlay is safe
      if (overlay.contains(node)) return;

      // Candidate Dawn modal elements (varies by version/customizations)
      const looksLikeMediaModal =
        node.matches?.('media-modal, product-modal, modal-dialog, .product-media-modal, .product-modal') ||
        node.querySelector?.('media-modal, product-modal, .product-media-modal, .product-modal');

      if (!looksLikeMediaModal) return;

      // Only claim while Quick View is open
      if (!isQuickViewOpen()) return;

      // If this modal is clearly part of real page main content, don't claim
      if (node.closest && node.closest('#MainContent')) return;

      try { node.setAttribute('data-nov-qv-owned', '1'); } catch (e) {}
      overlay._novOwnedNodes.add(node);
    };

    const obs = new MutationObserver((muts) => {
      for (const m of muts) {
        if (!m.addedNodes || !m.addedNodes.length) continue;
        m.addedNodes.forEach((n) => {
          maybeOwn(n);
          if (n && n.querySelectorAll) {
            n.querySelectorAll('media-modal, product-modal, modal-dialog, .product-media-modal, .product-modal')
              .forEach(maybeOwn);
          }
        });
      }
    });

    obs.observe(document.body, { childList: true, subtree: true });
    overlay._novOwnedObserver = obs;

    // Initial scan (just in case)
    document.body
      .querySelectorAll('media-modal, product-modal, modal-dialog, .product-media-modal, .product-modal')
      .forEach(maybeOwn);
  }

  function stopQuickViewModalOwnership(overlay) {
    if (!overlay) return;

    if (overlay._novOwnedObserver) {
      try { overlay._novOwnedObserver.disconnect(); } catch (e) {}
      overlay._novOwnedObserver = null;
    }

    // Remove ONLY what we owned
    try {
      document.querySelectorAll('[data-nov-qv-owned="1"]').forEach((n) => n.remove());
    } catch (e) {}

    if (overlay._novOwnedNodes) overlay._novOwnedNodes.clear();
  }

  // ---------------------------
  // Discover main product section id
  // ---------------------------
  async function discoverMainSectionId(handle, initialVariant) {
    const u = new URL(`/products/${handle}`, window.location.origin);
    if (initialVariant) u.searchParams.set('variant', initialVariant);

    const res = await fetch(u.toString(), { credentials: 'same-origin' });
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const productInfo = doc.querySelector('product-info[id^="MainProduct-"]');
    return productInfo?.id?.replace('MainProduct-', '') || null;
  }

  // ---------------------------
  // Variant sync to collection links
  // ---------------------------
  function updateParentLinks(handle, variantId) {
    if (!handle || !variantId) return;
    document.querySelectorAll(`a[href*="/products/${CSS.escape(handle)}"]`).forEach((a) => {
      try {
        const u = new URL(a.getAttribute('href'), window.location.origin);
        u.searchParams.set('variant', String(variantId));
        a.setAttribute('href', u.pathname + u.search + u.hash);
      } catch (e) {}
    });
  }

  function bindVariantObserver(overlay, handle) {
    const body = overlay.querySelector('.nov-qv-body');
    if (!body) return () => {};

    const findIdInput = () =>
      body.querySelector('form[data-type="add-to-cart-form"] input[name="id"]') ||
      body.querySelector('product-form form[action*="/cart/add"] input[name="id"]') ||
      body.querySelector('form[action*="/cart/add"] input[name="id"]') ||
      body.querySelector('input[name="id"]');

    const sync = () => {
      const input = findIdInput();
      const variantId = input?.value ? String(input.value).trim() : '';
      if (!variantId) return;

      overlay.setAttribute('data-nov-variant', variantId);
      updateParentLinks(handle, variantId);

      if (overlay.classList.contains('is-open')) {
        enableProductContext(handle, variantId);
        syncAnalyticsForModal(overlay, handle);
        refreshSnapCompareButtonState(overlay, handle);
        ensureQuickViewFallbackPrice(overlay);
        setTimeout(() => ensureQuickViewFallbackPrice(overlay), 150);
      }
    };

    // Initial sync
    sync();

    let mo;
    const attach = () => {
      const input = findIdInput();
      if (!input) return;

      mo?.disconnect();
      mo = new MutationObserver(sync);
      mo.observe(input, { attributes: true, attributeFilter: ['value'] });

      input.addEventListener('change', sync);
      input.addEventListener('input', sync);
    };

    attach();
    setTimeout(attach, 250);
    setTimeout(attach, 650);

    document.addEventListener('variant:change', sync);

    return () => {
      try { document.removeEventListener('variant:change', sync); } catch (e) {}
      try { mo?.disconnect(); } catch (e) {}
    };
  }

  // ==========================================================================
  // Snap Compare — Quick View button (writes to localStorage snap-compare-list)
  // ==========================================================================
  function safeJsonParse(str, fallback) {
    try { return JSON.parse(str); } catch (e) { return fallback; }
  }

  function safeJsonStringify(obj, fallback = '[]') {
    try { return JSON.stringify(obj); } catch (e) { return fallback; }
  }

  function getSnapCompareList() {
    const raw = localStorage.getItem('snap-compare-list');
    const list = safeJsonParse(raw, []);
    return Array.isArray(list) ? list : [];
  }

  function setSnapCompareList(list) {
    const key = 'snap-compare-list';
    const oldValue = localStorage.getItem(key);
    const newValue = safeJsonStringify(list, '[]');

    localStorage.setItem(key, newValue);

    // Fire a best-effort StorageEvent (same-tab listeners)
    try {
      const evt = new StorageEvent('storage', {
        key,
        oldValue,
        newValue,
        url: location.href,
        storageArea: localStorage
      });
      window.dispatchEvent(evt);
    } catch (e) {
      try { window.dispatchEvent(new Event('storage')); } catch (e2) {}
    }

    // App-ish update events
    const names = [
      'snap-compare:update',
      'snap-compare:updated',
      'snap-compare:change',
      'compare:update',
      'compare:updated',
      'compare:change'
    ];
    names.forEach((name) => {
      try { document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { key } })); } catch (e) {}
      try { window.dispatchEvent(new CustomEvent(name, { detail: { key } })); } catch (e) {}
    });
  }

  function findSnapProductByHandleInMeta(handle) {
    try {
      const meta = getMeta();
      const list = Array.isArray(meta?.products) ? meta.products : [];
      return list.find(p => p && p.handle === handle) || null;
    } catch (e) {
      return null;
    }
  }

  function getModalSelectedVariantId(overlay) {
    const body = overlay.querySelector('.nov-qv-body');
    if (!body) return null;

    const input =
      body.querySelector('form[data-type="add-to-cart-form"] input[name="id"]') ||
      body.querySelector('product-form form[action*="/cart/add"] input[name="id"]') ||
      body.querySelector('form[action*="/cart/add"] input[name="id"]') ||
      body.querySelector('input[name="id"]');

    const v = input?.value ? String(input.value).trim() : '';
    return v || null;
  }

  function upsertSnapCompareProduct(handle, productObj) {
    const list = getSnapCompareList();
    const id = productObj?.id;

    const idx = list.findIndex(p => p && (p.handle === handle || (id && p.id === id)));
    if (idx >= 0) list[idx] = productObj;
    else list.push(productObj);

    setSnapCompareList(list);
  }

  function removeSnapCompareProduct(handle, productGid) {
    const list = getSnapCompareList();
    const filtered = list.filter(p => {
      if (!p) return false;
      if (productGid && p.id === productGid) return false;
      if (handle && p.handle === handle) return false;
      return true;
    });
    setSnapCompareList(filtered);
  }

  function isInSnapCompare(handle, productGid) {
    const list = getSnapCompareList();
    return list.some(p => {
      if (!p) return false;
      if (productGid && p.id === productGid) return true;
      if (handle && p.handle === handle) return true;
      return false;
    });
  }

  function getModalFeaturedImageFromDOM(overlay) {
    const body = overlay.querySelector('.nov-qv-body');
    if (!body) return null;

    const img =
      body.querySelector('.product__media img') ||
      body.querySelector('.product-media-container img') ||
      body.querySelector('.media img') ||
      body.querySelector('img[src*="cdn.shopify.com"]');

    const url = img?.getAttribute('src') || '';
    if (!url) return null;

    let clean = url;
    try {
      const u = new URL(url, location.origin);
      clean = u.href;
    } catch (e) {}

    const w = parseInt(img.getAttribute('width') || '0', 10) || null;
    const h = parseInt(img.getAttribute('height') || '0', 10) || null;

    return {
      alt: img.getAttribute('alt') || null,
      preview: {
        image: {
          url: clean,
          width: w,
          height: h
        }
      }
    };
  }

  // --- DOM extraction helpers for Description + Type ---
  function getTextFromEl(el) {
    if (!el) return '';
    return (el.textContent || el.innerText || '').replace(/\s+/g, ' ').trim();
  }

  function getModalDescriptionFromDOM(overlay) {
    const body = overlay?.querySelector?.('.nov-qv-body');
    if (!body) return '';

    // 1) Native Dawn description
    const dawnDesc =
      body.querySelector('.product__description .rte') ||
      body.querySelector('.product__description') ||
      body.querySelector('.rte');

    let txt = getTextFromEl(dawnDesc);
    if (txt) return txt;

    // 2) EasyTabs / common tab content selectors
    const candidates = [
      '.easytabs-tab-content',
      '.easytabs-content',
      '.easytabs-panel',
      '.tabs-content',
      '.tab-content',
      '.tab-pane',
      '[role="tabpanel"]',
      '#tab-details',
      '#tab-description',
      '[id*="details"]',
      '[id*="description"]'
    ];

    for (const sel of candidates) {
      const el = body.querySelector(sel);
      txt = getTextFromEl(el);
      if (txt) return txt;
    }

    return '';
  }

  function getModalTypeFromDOM(overlay) {
    const body = overlay?.querySelector?.('.nov-qv-body');
    if (!body) return '';

    const direct =
      body.querySelector('[data-product-type]') ||
      body.querySelector('.product__type') ||
      body.querySelector('.product-type');

    let txt = getTextFromEl(direct);
    if (txt) return txt;

    // Scan for "Type: X"
    const labelEls = Array.from(body.querySelectorAll('p, div, span, li, dt'));
    for (const el of labelEls) {
      const t = getTextFromEl(el);
      if (!t) continue;
      const m = t.match(/^type\s*:\s*(.+)$/i);
      if (m && m[1]) return m[1].trim();
    }

    return '';
  }

  // --- OLD builder (kept as fallback) ---
  function buildSnapComparePayloadFromMetaAndModal(overlay, handle, variantNumericId) {
    const prod = findSnapProductByHandleInMeta(handle);
    if (!prod) return null;

    const productGid = prod.gid || (prod.id ? `gid://shopify/Product/${prod.id}` : null);
    if (!productGid) return null;

    const vNum = variantNumericId ? String(variantNumericId) : '';
    const vMeta = Array.isArray(prod.variants) ? prod.variants.find(v => String(v.id) === vNum) : null;

    const featuredMedia = prod.featuredMedia || getModalFeaturedImageFromDOM(overlay) || null;

    const variantImage = featuredMedia?.preview?.image?.url
      ? {
          url: featuredMedia.preview.image.url,
          width: featuredMedia.preview.image.width || null,
          height: featuredMedia.preview.image.height || null
        }
      : null;

    const variantPayload = vMeta ? {
      metafields: {},
      id: `gid://shopify/ProductVariant/${vMeta.id}`,
      title: vMeta.public_title || vMeta.name || '',
      price: String(vMeta.price || 0),
      compareAtPrice: null,
      availableForSale: true,
      sku: vMeta.sku || null,
      barcode: null,
      contextualPricing: {
        price: { amount: String((vMeta.price || 0) / 100), currencyCode: 'USD' },
        compareAtPrice: null
      },
      image: variantImage,
      currencyCode: 'USD'
    } : null;

    const domDesc = getModalDescriptionFromDOM(overlay) || '';
    const domType = getModalTypeFromDOM(overlay) || '';

    return {
      metafields: {},
      id: productGid,
      gid: productGid,
      title: prod.title || '',
      handle: prod.handle || handle,

      description: domDesc || prod.description || '',
      descriptionText: domDesc || prod.description || '',
      body: domDesc || prod.description || '',

      vendor: prod.vendor || '',
      tags: Array.isArray(prod.tags) ? prod.tags : [],

      type: domType || prod.type || '',
      productType: domType || prod.type || '',
      product_type: domType || prod.type || '',

      onlineStorePreviewUrl: prod.onlineStorePreviewUrl || `${location.origin}/products/${handle}`,
      featuredMedia,
      variant: variantPayload
    };
  }

  // --- NEW builder (primary) ---
  function getActiveCurrencyCode() {
    try {
      if (window.Shopify?.currency?.active) return String(window.Shopify.currency.active);
    } catch (e) {}
    try {
      const meta = getMeta();
      if (meta?.currency) return String(meta.currency);
    } catch (e) {}
    return 'USD';
  }

  async function fetchProductDotJs(handle) {
    const u = new URL(`/products/${handle}.js`, window.location.origin);
    u.searchParams.set('_', Date.now().toString());
    const res = await fetch(u.toString(), { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Failed /products/${handle}.js (${res.status})`);
    return await res.json();
  }

  function pickBestImageUrlFromProductJs(p, variant) {
    const vImg = variant?.featured_image?.src;
    if (vImg) return vImg;
    if (p?.featured_image) return p.featured_image;
    if (Array.isArray(p?.images) && p.images[0]) return p.images[0];
    return null;
  }

  function buildSnapComparePayloadFromProductJs(p, selectedVariantId, overlay) {
    if (!p || !p.id) return null;

    const currencyCode = getActiveCurrencyCode();
    const productGid = `gid://shopify/Product/${p.id}`;

    const variantNum = selectedVariantId ? String(selectedVariantId) : null;
    const variant = Array.isArray(p.variants)
      ? (p.variants.find(v => String(v.id) === variantNum) || p.variants[0] || null)
      : null;

    // product.js variant.price is CENTS
    const priceCents = variant ? (Number(variant.price) || 0) : 0;
    const compareAtCents = variant && variant.compare_at_price ? (Number(variant.compare_at_price) || 0) : null;

    const imgUrl = pickBestImageUrlFromProductJs(p, variant);

    const featuredMedia = imgUrl
      ? { alt: null, preview: { image: { url: imgUrl, width: null, height: null } } }
      : (getModalFeaturedImageFromDOM(overlay) || null);

    const variantPayload = variant ? {
      metafields: {},
      id: `gid://shopify/ProductVariant/${variant.id}`,
      title: variant.public_title || variant.title || '',
      // ✅ Store cents (Snap Compare often divides by 100)
      price: String(priceCents),
      compareAtPrice: compareAtCents != null ? String(compareAtCents) : null,
      availableForSale: !!variant.available,
      sku: variant.sku || null,
      barcode: variant.barcode || null,
      contextualPricing: {
        price: { amount: String(priceCents / 100), currencyCode },
        compareAtPrice: compareAtCents != null ? { amount: String(compareAtCents / 100), currencyCode } : null
      },
      image: imgUrl ? { url: imgUrl, width: null, height: null } : null,
      currencyCode
    } : null;

    const tags = Array.isArray(p.tags)
      ? p.tags
      : (typeof p.tags === 'string'
        ? p.tags.split(',').map(t => t.trim()).filter(Boolean)
        : []);

    // Prefer DOM-derived “Details/Description” + Type
    const domDescription = getModalDescriptionFromDOM(overlay);
    const domType = getModalTypeFromDOM(overlay);

    const shopifyHtml = p.body_html || '';
    const shopifyText = (shopifyHtml ? shopifyHtml.replace(/<[^>]*>/g, ' ') : '').replace(/\s+/g, ' ').trim();

    const descriptionFinal = domDescription || shopifyText || '';
    const typeFinal = domType || (p.product_type || '');

    return {
      metafields: {},

      id: productGid,
      gid: productGid,

      title: p.title || '',
      handle: p.handle || '',

      description: descriptionFinal,
      descriptionText: descriptionFinal,
      body: descriptionFinal,
      bodyText: descriptionFinal,
      body_html: shopifyHtml,
      descriptionHtml: shopifyHtml,

      vendor: p.vendor || '',
      tags,

      type: typeFinal,
      productType: typeFinal,
      product_type: typeFinal,

      onlineStorePreviewUrl: `${location.origin}/products/${p.handle || ''}`,
      featuredMedia,
      variant: variantPayload
    };
  }

  // --- Button injection for Quick View ---
  function ensureSnapCompareButton(overlay, handle) {
    const body = overlay.querySelector('.nov-qv-body');
    if (!body) return;

    const buttonsWrap =
      body.querySelector('.product-form__buttons') ||
      body.querySelector('product-form') ||
      body.querySelector('form[action*="/cart/add"]') ||
      body;

    const payWrap = buttonsWrap.querySelector('.shopify-payment-button');
    const mount = payWrap || buttonsWrap;

    if (mount.querySelector('[data-nov-snap-compare-qv]')) return;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.setAttribute('data-nov-snap-compare-qv', '1');
    btn.className = 'sp-button sp-button-compare button button--secondary';

    const label = document.createElement('span');
    label.textContent = 'Add to compare';
    btn.appendChild(label);

    function updateLabel() {
      const metaProd = findSnapProductByHandleInMeta(handle);
      const productGid = metaProd?.gid || (metaProd?.id ? `gid://shopify/Product/${metaProd.id}` : null);
      const active = isInSnapCompare(handle, productGid);

      label.textContent = active ? 'Remove from compare' : 'Add to compare';
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    }

    btn.addEventListener('click', async () => {
      const metaProd = findSnapProductByHandleInMeta(handle);
      const metaGid = metaProd?.gid || (metaProd?.id ? `gid://shopify/Product/${metaProd.id}` : null);

      const active = isInSnapCompare(handle, metaGid);
      if (active) {
        removeSnapCompareProduct(handle, metaGid);
        updateLabel();
        return;
      }

      const variantId = getModalSelectedVariantId(overlay);
      let payload = null;

      try {
        const p = await fetchProductDotJs(handle);
        payload = buildSnapComparePayloadFromProductJs(p, variantId, overlay);
      } catch (err) {
        console.warn('[NOV-QV] Failed to build payload via /products/<handle>.js:', err);
        payload = buildSnapComparePayloadFromMetaAndModal(overlay, handle, variantId);
      }

      if (!payload) {
        console.warn('[NOV-QV] Snap Compare payload could not be built for:', handle);
        return;
      }

      upsertSnapCompareProduct(handle, payload);
      updateLabel();
    });

    mount.appendChild(btn);
    updateLabel();
    overlay._novSnapBtnRefresh = updateLabel;
  }

  function refreshSnapCompareButtonState(overlay, handle) {
    if (overlay._novSnapBtnRefresh) {
      try { overlay._novSnapBtnRefresh(); } catch (e) {}
    }
  }

  // ---------------------------
  // Modal scaffold
  // ---------------------------
  function ensureModal() {
    let overlay = document.querySelector('.nov-qv-overlay');
    if (overlay) return overlay;

    overlay = document.createElement('div');
    overlay.className = 'nov-qv-overlay';
    overlay.innerHTML = `
      <div class="nov-qv-panel" role="dialog" aria-modal="true" aria-label="Quick view">
        <div class="nov-qv-close">
          <button type="button" aria-label="Close quick view">×</button>
        </div>
        <div class="nov-qv-body"></div>
      </div>
    `;
    document.body.appendChild(overlay);

    // Basic modal styles (minimal)
    if (!document.querySelector('#nov-qv-style')) {
      const st = document.createElement('style');
      st.id = 'nov-qv-style';
      st.textContent = `
        .nov-qv-overlay{position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:999999;display:none;}
        .nov-qv-overlay.is-open{display:block;}
        .nov-qv-panel{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);width:min(1100px,92vw);max-height:92vh;background:#fff;border-radius:14px;overflow:auto;}
        .nov-qv-close{position:sticky;top:0;display:flex;justify-content:flex-end;background:#fff;z-index:2;padding:10px 12px;border-bottom:1px solid rgba(0,0,0,.06);}
        .nov-qv-close button{font-size:26px;line-height:1;background:transparent;border:0;cursor:pointer;padding:6px 10px;}
        .nov-qv-body{padding:0;}
        .nov-qv-overlay{position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:999999;display:none;}
        .nov-qv-overlay.is-open{display:block;}
        .nov-qv-panel{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);width:min(1100px,92vw);max-height:92vh;background:#fff;border-radius:14px;overflow:auto;}
        .nov-qv-close{position:sticky;top:0;display:flex;justify-content:flex-end;background:#fff;z-index:2;padding:10px 12px;border-bottom:1px solid rgba(0,0,0,.06);}
        .nov-qv-close button{font-size:26px;line-height:1;background:transparent;border:0;cursor:pointer;padding:6px 10px;}
        .nov-qv-body{padding:0;}
        html.nov-qv-open, body.nov-qv-open{overflow:hidden!important;}

        @media screen and (max-width: 989px) {
          html body .nov-qv-overlay.is-open .nov-qv-body .nov-type-line[data-nov-type-line] {
            display: grid !important;
            grid-template-columns: auto minmax(0, 1fr) !important;
            column-gap: 0.35rem !important;
            align-items: baseline !important;
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            line-height: 1.25 !important;
            margin: 0.4rem 0 0.6rem !important;
          }

          html body .nov-qv-overlay.is-open .nov-qv-body .nov-type-line__variant[data-nov-type-variant] {
            display: block !important;
            min-width: 0 !important;
            max-width: 100% !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }
        }
        html.nov-qv-open, body.nov-qv-open{overflow:hidden!important;}
      `;
      document.head.appendChild(st);
    }

    function close() {
      overlay.classList.remove('is-open');
      document.documentElement.classList.remove('nov-qv-open');
      document.body.classList.remove('nov-qv-open');

      // ✅ Remove ONLY Dawn media/gallery modals spawned during Quick View
      stopQuickViewModalOwnership(overlay);

      disableProductContext();

      const body = overlay.querySelector('.nov-qv-body');
      if (body) body.innerHTML = '';

      overlay.removeAttribute('data-nov-handle');
      overlay.removeAttribute('data-nov-variant');
      overlay.removeAttribute('data-nov-section-id');

      overlay._novSnapBtnRefresh = null;

      if (overlay._novCleanup) {
        try { overlay._novCleanup(); } catch (e) {}
        overlay._novCleanup = null;
      }

      enforceKillAndClean();
    }

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close();
    });
    overlay.querySelector('.nov-qv-close button').addEventListener('click', close);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) close();
    });

    overlay._novClose = close;
    return overlay;
  }

  function ensureQuickViewFallbackPrice(overlay) {
    const body = overlay?.querySelector?.('.nov-qv-body');
    if (!body) return;

    // Find the price wrapper for Dawn main product section
    const priceRoot =
      body.querySelector('#price-template--' + (overlay.getAttribute('data-nov-section-id') || '')) ||
      body.querySelector('[id^="price-template--"]') ||
      body.querySelector('.product__info-wrapper .price') ||
      body.querySelector('.price');

    if (!priceRoot) return;

    // If we already injected fallback, reuse it
    let fallback = priceRoot.querySelector('.nov-qv-fallback-price');
    if (!fallback) {
      fallback = document.createElement('div');
      fallback.className = 'nov-qv-fallback-price';
      fallback.style.fontWeight = '600';
      fallback.style.marginTop = '0.2rem';
      priceRoot.appendChild(fallback);
    }

    // Extract whichever price is actually visible in markup
    const sale = priceRoot.querySelector('.price__sale .price-item--sale.price-item--last');
    const reg = priceRoot.querySelector('.price__regular .price-item--regular');
    const regInSale = priceRoot.querySelector('.price__sale .price-item--regular');

    const saleText = sale?.textContent?.trim();
    const regText = reg?.textContent?.trim();
    const regWasText = regInSale?.textContent?.trim();

    if (saleText) {
      const compare = regWasText ? ` <span style="opacity:.65;text-decoration:line-through;margin-left:.5rem;">${regWasText}</span>` : '';
      fallback.innerHTML = `${saleText}${compare}`;
    } else if (regText) {
      fallback.textContent = regText;
    } else {
      const any = priceRoot.querySelector('.price-item');
      fallback.textContent = any?.textContent?.trim() || '';
    }
  }

  // ---------------------------
  // Open Quick View
  // ---------------------------
  async function openQuickView(productPathOrUrl) {
    enforceKillAndClean();

    const overlay = ensureModal();
    const body = overlay.querySelector('.nov-qv-body');

    const path = toRelative(productPathOrUrl);
    const handle = getHandleFromPath(path);
    const initialVariant = getVariantFromPath(path);

    overlay.setAttribute('data-nov-handle', handle || '');
    if (initialVariant) overlay.setAttribute('data-nov-variant', initialVariant);

    overlay.classList.add('is-open');
    document.documentElement.classList.add('nov-qv-open');
    document.body.classList.add('nov-qv-open');

    // ✅ Start owning any body-level Dawn media/gallery modal created during QV
    startQuickViewModalOwnership(overlay);

    // Spoof URL/body class early
    enableProductContext(handle, initialVariant);

    body.innerHTML = `<div style="padding:18px;">Loading…</div>`;

    if (!handle) {
      renderError(body, 'Quick view error', 'Could not identify handle from: ' + path);
      return;
    }

    let mainSectionId = null;
    try {
      mainSectionId = await discoverMainSectionId(handle, initialVariant);
    } catch (e) {
      renderError(body, 'Quick view error', 'Failed to discover section id:\n' + String(e));
      return;
    }

    if (!mainSectionId) {
      renderError(body, 'Quick view error', 'Could not find MainProduct-<sectionId> in product HTML.');
      return;
    }

    const secUrl = new URL(`/products/${handle}`, window.location.origin);
    secUrl.searchParams.set('sections', mainSectionId);
    if (initialVariant) secUrl.searchParams.set('variant', initialVariant);

    let text = '';
    try {
      const res = await fetch(secUrl.toString(), { credentials: 'same-origin' });
      text = await res.text();

      let data;
      try { data = JSON.parse(text); }
      catch {
        renderError(body, 'Quick view error', 'Expected JSON from ?sections=. Got:\n\n' + text.slice(0, 1200));
        return;
      }

      const html = data[mainSectionId] || '';
      if (!html.trim()) {
        renderError(body, 'Quick view returned empty HTML', `Section id: ${mainSectionId}`);
        return;
      }

      body.innerHTML = `<div data-nov-section="${mainSectionId}">${html}</div>`;

      // Ensure meta is correct for this product
      syncAnalyticsForModal(overlay, handle);

      // Execute any inline scripts
      executeScripts(body);

      // Save section id on overlay for price lookup helper
      overlay.setAttribute('data-nov-section-id', mainSectionId);

      // Inject/update fallback price (bypasses BSS)
      ensureQuickViewFallbackPrice(overlay);
      setTimeout(() => ensureQuickViewFallbackPrice(overlay), 250);
      setTimeout(() => ensureQuickViewFallbackPrice(overlay), 650);

      // Nudge init events
      nudgeInit();
      setTimeout(nudgeInit, 350);
      setTimeout(nudgeInit, 900);

      // Variant observer (keeps meta + links updated)
      if (overlay._novCleanup) overlay._novCleanup();
      overlay._novCleanup = bindVariantObserver(overlay, handle);

      // Cleanup any stray injections
      setTimeout(enforceKillAndClean, 50);
      setTimeout(enforceKillAndClean, 250);

      // Add Snap Compare button
      ensureSnapCompareButton(overlay, handle);
    } catch (e) {
      renderError(body, 'Quick view request failed', String(e) + '\n\nPreview:\n' + text.slice(0, 800));
    }
  }

  // ---------------------------
  // Intercept Globo quick view triggers (capture phase)
  // ---------------------------
  function interceptQuickViewEvent(e) {
    const t = e.target;
    if (!t || !t.closest) return;

    const a =
      t.closest('a.open-quick-view') ||
      t.closest('a[onclick*="quickView("]') ||
      t.closest('a[onclick*="globofilter.quickView"]');

    if (!a) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    if (a.hasAttribute('onclick')) {
      a.setAttribute('data-nov-onclick', a.getAttribute('onclick'));
      a.removeAttribute('onclick');
    }

    const stored = a.getAttribute('data-nov-onclick') || '';
    const match = stored.match(/quickView\(['"]([^'"]+)['"]\)/i);
    const p = match?.[1] || a.getAttribute('href') || '';

    openQuickView(toRelative(p));
    return false;
  }

  document.addEventListener('pointerdown', interceptQuickViewEvent, true);
  document.addEventListener('click', interceptQuickViewEvent, true);

  // Manual testing hook
  window.NOV_openQuickView = openQuickView;
})();
